// dateFormatting.js
//
// Single source of truth for the day/month abbreviations used to build
// filenames and spreadsheet values across the app.
//
// WHY THIS FILE EXISTS: Documents.js and GeneratePDF.js each used to have
// their own separate copy of these abbreviations. Documents.js builds the
// GMass spreadsheet's Attachment1/SubjectLine values (which filename GMass
// should look for and attach), while GeneratePDF.js builds the ACTUAL PDF
// filenames. If the two copies ever drift apart — even slightly, like
// "Sep" vs "Sept." — GMass silently fails to find and attach the right
// file. This has broken real confirmation-email sends twice: once for day
// abbreviations (Thu vs Thursday), and once for month abbreviations
// (Sep vs Sept., and short vs spelled-out for March-July). Importing from
// this one file instead of keeping separate copies makes that class of
// bug structurally impossible going forward.
//
// If these abbreviations ever need to change, change them ONLY here.

export const DAYS_OF_WEEK = {
    Sunday: 'Sun.',
    Monday: 'Mon.',
    Tuesday: 'Tues.',
    Wednesday: 'Wed.',
    Thursday: 'Thurs.',
    Friday: 'Fri.',
    Saturday: 'Sat.',
};

// Index 0 = January ... 11 = December, matching Date.getMonth()/getUTCMonth().
export const MONTHS_FOR_FILE = [
    'Jan.', 'Feb.', 'March', 'April', 'May', 'June', 'July',
    'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.',
];

/**
 * Formats a Date into "Day., Month Date, Year" using the shared
 * abbreviations above — e.g. "Wed., Sept. 30, 2026" or "Wed., March 4, 2026".
 * Used for both the GMass spreadsheet's expected filename text (Documents.js)
 * and the real PDF filenames (GeneratePDF.js) — they must always match.
 */
export function formatDayMonthDateYear(date) {
    const dayName = DAYS_OF_WEEK[date.toLocaleDateString('en-US', { weekday: 'long' })];
    const monthName = MONTHS_FOR_FILE[date.getMonth()];
    return `${dayName}, ${monthName} ${date.getDate()}, ${date.getFullYear()}`;
}

/**
 * Same as above, but for code paths working in UTC (Documents.js uses
 * getUTCDay()/getUTCMonth()/getUTCDate()/getUTCFullYear() rather than the
 * local-time equivalents GeneratePDF.js uses). Kept as a separate explicit
 * function rather than a flag, so it's obvious at the call site which
 * timezone behavior is in effect.
 */
export function formatDayMonthDateYearUTC(date) {
    const days = ['Sun.', 'Mon.', 'Tues.', 'Wed.', 'Thurs.', 'Fri.', 'Sat.'];
    const dayName = days[date.getUTCDay()];
    const monthName = MONTHS_FOR_FILE[date.getUTCMonth()];
    return `${dayName}, ${monthName} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
}
